import { defineStore } from 'pinia'
import { api } from 'boot/axios'
import { LocalStorage, Notify, Cookies } from 'quasar'
import { i18n } from 'boot/i18n';
import { useOrganizationStore } from 'stores/organization';
import { useAppUtils } from "@/composables/useAppUtils";
import { getCssVar } from 'quasar'

const createDefaultUserSettings = () => ({
  isDarkTheme: false,
  language: 'en-US',
  timezone: 'Europe/Berlin',
  dateFormat: 'DD.MM.YYYY',
  timeFormat: 'HH:mm',
  useGrid: false,
  activeTab: 'details',
  primaryColor: getCssVar('primary')
})

const normalizeUserSettings = (settings) => {
  let parsedSettings = settings

  if (typeof parsedSettings === 'string') {
    try {
      parsedSettings = JSON.parse(parsedSettings)
    }
    catch {
      parsedSettings = null
    }
  }

  return {
    ...createDefaultUserSettings(),
    ...(parsedSettings || {})
  }
}

export const useUserStore = defineStore('user', {
  id: 'user',
  state: () => ({
    items: [],
    item: {},
    user: null,
    canInvite: null,
    searchedUser: null,
    availableRoles: [],
    userRoles: [],
    pendingInvitations: [],
    me: LocalStorage.getItem('me') || null,
    token: Cookies.get('authToken') || null,
    loading: false,
    loadingUser: false,
    loadingPhone: false,
    loadingEmail: false,
    loadingInvitations: false,
    loadingUserRoles: false,
    resetPasswordURL: null,
    setPasswordURL: null,
    resetEmailMessage: null,
    pagination: {
      page: 1,
      orderDir: "desc",
      deleted: false,
      search: "",
      orderBy: "id",
      rowsPerPage: 12,
      rowsNumber: 1
    },
    userSettings: normalizeUserSettings(LocalStorage.getItem('userSettings'))
  }),

  getters: {
    isLoggedIn: () => {
      return Cookies.has('authToken') ? true : false
    },
    getToken: () => {
      return Cookies.get('authToken')
    },
    cookieOptions: () => {
      let cookiesOptions = {
        path: '/'
      }
      if (import.meta.env.VITE_APP_NODE_ENV !== 'local') {
        cookiesOptions.domain = import.meta.env.VITE_APP_DOMAIN
        cookiesOptions.sameSite = 'None'
        cookiesOptions.secure = true
      }
      return cookiesOptions
    },
    defaultDateTimeFormat: (state) => {
      return `${state.userSettings.dateFormat} ${state.userSettings.timeFormat}`
    },

    tableColumns: () => {
      const { t } = i18n
      const { formatDate } = useAppUtils();
      return [
        { name: 'id', label: t('table-columns.id'), align: 'left', field: 'id', },
        { name: 'first_name', label: t('table-columns.first-name'), align: 'center', field: 'first_name', format: (row) => row || 'N/A' },
        { name: 'last_name', label: t('table-columns.last-name'), field: 'last_name', format: (row) => row || 'N/A' },
        { name: 'username', label: t('table-columns.username'), field: 'username' },
        { name: 'email', label: t('table-columns.email'), align: 'center', field: 'email', format: (row) => row || 'N/A' },
        { name: 'phone_number', label: t('table-columns.phone-number'), field: 'phone_number', format: (row) => row || 'N/A' },
        { name: 'updated_at', label: t('table-columns.updated-at'), field: 'updated_at', align: 'center', format: (row) => formatDate(row) },
        { name: 'actions', label: t('table-columns.actions'), field: '', align: 'right', }
      ]
    },
    filterColumns: () => {
      const { t } = i18n
      return [
        { label: t('table-columns.email'), value: "email" },
        { label: t('table-columns.created-at'), value: "created_at" },
        { label: t('table-columns.updated-at'), value: "updated_at" },
        { label: t('table-columns.first-name'), value: "first_name" },
        { label: t('table-columns.last-name'), value: "last_name" },
        { label: t('table-columns.username'), value: "username" }
      ]
    },
    exportColumns: () => {
      const { t } = i18n
      return [
        { label: t('table-columns.name'), value: "name" },
        { label: t('table-columns.display-name'), value: "display_name" },
        { label: t('table-columns.description'), value: "description" },
        { label: t('table-columns.created-at'), value: "created_at" }
      ]
    }
  },

  actions: {
    async getItems() {
      this.loading = true
      try {
        const { rowsPerPage, ...rest } = this.pagination
        const newPagin = { perPage: rowsPerPage, ...rest }
        const { data } = await api.get('/control/app/users', { params: { ...newPagin } })
        this.loading = false
        this.pagination.rowsNumber = data.meta.total
        this.pagination.rowsPerPage = data.meta.per_page
        this.pagination.page = data.meta.current_page
        this.items = data.data
      }
      catch (error) {
        this.loading = false
        throw error
      }
    },

    async loadMyUser() {
      this.loading = true
      try {
        const { data } = await api.get('/me')
        const userSettings = normalizeUserSettings(data.settings)
        this.loading = false
        LocalStorage.set('me', data)
        const { setMyOrganization } = useOrganizationStore();
        setMyOrganization(data)
        this.me = data
        this.userSettings = userSettings
        LocalStorage.set('userSettings', userSettings)
      }
      catch (error) {
        this.loading = false
        throw error
      }
    },

    setActiveTab(tab) {
      this.userSettings.activeTab = tab
      LocalStorage.set('userSettings', this.userSettings)
      this.updateUserSettings()
    },

    toggleGrid() {
      this.userSettings.useGrid = !this.userSettings.useGrid
      LocalStorage.set('userSettings', this.userSettings)
      this.updateUserSettings()
    },

    setPrimaryColor(color) {
      this.userSettings.primaryColor = color
      LocalStorage.set('userSettings', this.userSettings)
      this.updateUserSettings()
    },

    toggleStoreTheme() {
      this.userSettings.isDarkTheme = !this.userSettings.isDarkTheme
      LocalStorage.set('userSettings', this.userSettings)
      this.updateUserSettings()
    },

    setUserLanguage(lang) {
      this.userSettings.language = lang
      LocalStorage.set('userSettings', this.userSettings)
      this.updateUserSettings()
    },

    setUserTimezone(tz) {
      this.userSettings.timezone = tz
      LocalStorage.set('userSettings', this.userSettings)
      this.updateUserSettings()
    },


    setUserDateFormat(df) {
      this.userSettings.dateFormat = df
      LocalStorage.set('userSettings', this.userSettings)
      this.updateUserSettings()
    },

    setUserTimeFormat(tf) {
      this.userSettings.timeFormat = tf
      LocalStorage.set('userSettings', this.userSettings)
      this.updateUserSettings()
    },

    async updateUserSettings() {
      this.loading = true
      try {
        await api.patch('/users/profile/settings', { settings: JSON.stringify(this.userSettings) })
        this.loading = false
      }
      catch (error) {
        this.loading = false
        throw error
      }
    },
    async searchUsersByEmail(email) {
      this.loading = true
      try {
        const { data } = await api.get('control/app/users/search/all', { params: { email } })
        this.loading = false
        this.canInvite = data.data?.can_invite_to
        this.searchedUser = data.data?.user
      }
      catch (error) {
        this.loading = false
        throw error
      }
    },

    async checkPhoneNumber(phone_number) {

      this.loadingPhone = true
      try {
        await api.post('services/communication/number_lookup', { phone_number })
        this.loadingPhone = false
        return true
      }
      catch (error) {
        this.loadingPhone = false
        return false
      }
    },

    async getAvailableRoles() {
      this.loadingUserRoles = true
      try {
        const { data } = await api.get('roles/', { params: { all: true } })
        this.loadingUserRoles = false
        this.availableRoles = data
      }
      catch (error) {
        this.loadingUserRoles = false
        throw error
      }
    },

    async insertItem(user) {
      this.loading = true
      try {
        await api.post('/control/app/users/', user)
        this.loading = false
      }
      catch (error) {
        this.loading = false
        throw error
      }
    },

    async getItem(id) {
      this.loadingUser = true
      try {
        const { data } = await api.get('/control/app/users/' + id)
        this.loadingUser = false
        this.item = data
      }
      catch (error) {
        this.loadingUser = false
        throw error
      }
    },

    async updateItem(user) {
      console.log(user)
      this.loading = true
      try {
        await api.patch('/control/app/users/' + user.id, user.values)
        this.loading = false
      }
      catch (error) {
        this.loading = false
        throw error
      }
    },

    async deleteItem(user) {
      this.loading = true
      try {
        const { data } = await api.delete('/control/app/users/' + user.id)
        Notify.create({
          message: data.message,
          type: 'positive',
        })
        this.getItems()
        this.loading = false
      }
      catch (error) {
        this.loading = false
        throw error
      }
    },

    async updateProfile(user) {
      this.loading = true
      try {
        const { data } = await api.patch('users/profile/update', user)
        this.loading = false
        Notify.create({
          message: data.message,
          type: 'positive',
        })
      }
      catch (error) {
        this.loading = false
        throw error
      }
    },

    async changeUserPassword(item) {
      this.loading = true
      try {
        const { data } = await api.patch('users/profile/password', item)
        Notify.create({
          message: data.message,
          type: 'positive',
        })
        this.loading = false
      }
      catch (error) {
        this.loading = false
        throw error
      }
    },

    async changePhoneNumber(item) {
      this.loadingPhone = true
      try {
        const { data } = await api.patch('users/profile/phone_number', item)
        Notify.create({
          message: data.message,
          type: 'positive',
        })
        this.loadingPhone = false
      }
      catch (error) {
        this.loadingPhone = false
        throw error
      }
    },

    async changeEmail(item) {
      this.loadingEmail = true
      try {
        const { data } = await api.patch('users/profile/email', item)
        Notify.create({
          message: data.message,
          type: 'positive',
        })
        this.loadingEmail = false
      }
      catch (error) {
        this.loadingEmail = false
        throw error
      }
    },

    async login(user) {
      this.loading = true
      const orgStore = useOrganizationStore();
      try {
        const { data } = await api.post('/auth/login', user)
        this.loading = false
        orgStore.myOrganization = null
        this.me = null
        api.defaults.headers.common['Authorization'] = 'Bearer ' + data.data.token
        Cookies.set('authToken', data.data.token, { expires: 7, ...this.cookieOptions })
        return data
      }
      catch (error) {
        this.loading = false
        throw error
      }
    },


    async logout() {
      this.loading = true
      try {
        await api.post('/auth/logout')
        this.loading = false
      }
      catch (error) {
        this.loading = false
        throw error
      }
      finally {
        Cookies.remove('authToken', this.cookieOptions)
        this.removeLocalStorage()
      }
    },

    async forgotPassword(email) {
      this.loading = true
      try {
        const { data } = await api.post('/auth/password/forgot', email)
        this.resetEmailMessage = data.data.status
        Notify.create({
          message: data.message,
          type: 'positive',
        })
        this.loading = false
      }
      catch (error) {
        this.loading = false
        throw error
      }
    },

    async checkSetToken(id, token) {
      this.loading = true
      try {
        const { data } = await api.post(`/users/${id}/password/edit/${token}`)
        this.setPasswordURL = data.data.submit_url
        this.loading = false
      }
      catch (error) {
        this.loading = false
        throw error
      }
    },

    async checkResetToken(id, token) {
      this.loading = true
      try {
        const { data } = await api.post(`/auth/password/reset/${id}/${token}`)
        this.resetPasswordURL = data.data.submit_url
        this.loading = false
      }
      catch (error) {
        this.loading = false
        throw error
      }
    },

    async resetPassword(url, body) {
      this.loading = true
      try {
        const { data } = await api.post(url, body)
        Notify.create({
          message: data.message,
          type: 'positive',
        })
        this.loading = false
      }
      catch (error) {
        this.loading = false
        throw error
      }
    },

    async setPassword(url, body) {
      this.loading = true
      try {
        const { data } = await api.post(url, body)
        Notify.create({
          message: data.message,
          type: 'positive',
        })
        this.loading = false
      }
      catch (error) {
        this.loading = false
        throw error
      }
    },

    updateLocalUser(user) {
      const newUser = { ...this.me, ...user }
      LocalStorage.set('me', newUser)
    },
    removeLocalStorage() {
      LocalStorage.removeItem('me')
      LocalStorage.removeItem('myOrganization')
      LocalStorage.removeItem('userSettings')
    }
  },
})
